// ── User detail ("/users/:id") — the user record (Wave 1) ──────────────────
// The user record header (name + role + status + scope) with role-gated Edit /
// Deactivate affordances. The user is read from the users list (no single GET
// /users/:id exists) routed through useHonestQuery -> <HonestState>: a gate /
// permission-denied / unavailable / error NEVER reads as an empty shell, and a
// not-found id renders an honest "User not found", NEVER a leak.
//
// THE ID IS A STRING — passed VERBATIM (never Number()'d). STAFF-ONLY (the route
// manifest denies non-staff). Edit opens the same form modal; Deactivate opens
// the confirm modal; both toast ONLY after the backend confirms.

import { Badge, Button, DetailSurface, humanizeLabel, StatusDot, useToast } from "@vjal/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useUserLifecycleMutations } from "../../hooks/useCoachingQueries";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingUserDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { UserFormModal, type UserFormValues } from "./user-form-modal";
import { userWriteBody } from "./users-write";
import { UserDeactivateModal } from "./user-deactivate-modal";
import { findUser, isValidUserId, userDisplayName } from "./user-detail-helpers";
const services = getCoachingServices();

export function UserDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useUserLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "users:update").allowed;

  const userId = liveRecordIdFromPath(routePath, "users");
  const validId = isValidUserId(userId);

  const [editOpen, setEditOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const query = useHonestQuery<ListResponse<CoachingUserDto>>(
    coachingQueryKeys.list("users.list", { include_deactivated: "true" }),
    () => services.users.list({ include_deactivated: "true" }),
    {
      enabled: validId,
      isEmpty: (data) => (validId ? findUser(data.items, userId as string) === null : false)
    }
  );
  // Stable reference across react-query re-renders (so the edit form's reset
  // effect, keyed on `user`, doesn't clobber in-progress edits).
  const items = query.data?.items;
  const user = useMemo(
    () => (validId ? findUser(items ?? [], userId as string) : null),
    [validId, items, userId]
  );

  function submitEdit(values: UserFormValues) {
    if (!user) return Promise.reject(new Error("No user loaded"));
    return mutations.update
      .mutateAsync({
        userId: user.id,
        body: userWriteBody(values)
      })
      .then((updated) => {
        setEditOpen(false);
        mutations.update.reset();
        toast.success(`User ${updated.name ?? values.name} updated`);
        return updated;
      });
  }

  function submitDeactivate() {
    if (!user) return Promise.reject(new Error("No user loaded"));
    return mutations.deactivate.mutateAsync(user.id).then((result) => {
      setDeactivateOpen(false);
      mutations.deactivate.reset();
      toast.success(`User ${userDisplayName(user)} deactivated`);
      return result;
    });
  }

  if (!validId || query.posture !== null || !user) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="User detail" className="col-span-12" data-testid="user-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "User not found" : undefined}
            message={notFound ? "This user doesn't exist, or isn't in your workspace." : undefined}
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/users" className="text-link no-underline hover:underline">
                  Back to users
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  const lifecycleStatus = user.membershipStatus ?? user.status;
  const inactive = lifecycleStatus === "inactive" || lifecycleStatus === "removed";
  return (
    <section aria-label="User detail" className="col-span-12 flex flex-col gap-4" data-testid="user-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 user-detail IR. */}
      <DetailSurface
        title={
          <span
            className="inline-flex flex-wrap items-center gap-2"
            aria-label={user.role ? `${userDisplayName(user)}, ${humanizeLabel(user.role)}` : userDisplayName(user)}
          >
            <span>{userDisplayName(user)}</span>
            {user.role ? <Badge className="shrink-0">{humanizeLabel(user.role)}</Badge> : null}
          </span>
        }
        actions={
          allowUpdate ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setEditOpen(true)} aria-label="Edit user">
                Edit user
              </Button>
              <Button
                variant="danger"
                className={destructiveButtonClass}
                onClick={() => setDeactivateOpen(true)}
                disabled={inactive}
                aria-label="Open deactivate confirmation"
              >
                Deactivate
              </Button>
            </div>
          ) : undefined
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/users" className="text-link no-underline hover:underline">
                ← All users
              </Link>
            ),
          },
          {
            id: "status",
            label: "Status",
            value: (
              <span className="inline-flex items-center gap-2 text-sm">
                <StatusDot tone={lifecycleStatus === "active" ? "ok" : lifecycleStatus === "suspended" ? "warn" : "idle"} />
                {humanizeLabel(lifecycleStatus ?? "unknown")}
              </span>
            ),
          },
          {
            id: "email",
            label: "Email",
            value: user.email ?? "—",
          },
          {
            id: "scope",
            label: "Scope",
            value: user.scope_label ?? "—",
          },
          {
            id: "reference",
            label: "Reference",
            value: user.id,
            mono: true,
          },
        ]}
        titleExactName="User title"
      />

      {allowUpdate && (
        <>
          <UserFormModal
            open={editOpen}
            mode="edit"
            actorRole={role}
            user={user}
            onClose={() => {
              setEditOpen(false);
              mutations.update.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.update.isPending}
            submitError={mutations.update.error}
          />
          <UserDeactivateModal
            open={deactivateOpen}
            user={user}
            onClose={() => {
              setDeactivateOpen(false);
              mutations.deactivate.reset();
            }}
            onConfirm={submitDeactivate}
            submitting={mutations.deactivate.isPending}
            submitError={mutations.deactivate.error}
          />
        </>
      )}
    </section>
  );
}
